import { useState, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import {
    FaClock,
    FaCheckCircle,
    FaBan
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import ClientEOIService from '../../../services/ClientEOIService';
import ContractService from '../../../services/ContractService';

const useValidationRequest = () => {
    const [eois, setEois] = useState([]);
    const [filteredEois, setFilteredEois] = useState([]);
    const [activeFilter, setActiveFilter] = useState('PENDING');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEOI, setSelectedEOI] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);
    const [creatingContractId, setCreatingContractId] = useState(null);
    const [isApproving, setIsApproving] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedEOIForReject, setSelectedEOIForReject] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');

    // États pour Lightbox
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        fetchClientEOIs();
    }, []);

    useEffect(() => {
        filterEOIs();
    }, [eois, activeFilter]);

    const fetchClientEOIs = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await ClientEOIService.getAllClientEOIs();
            setEois(response);
        } catch (err) {
            setError(err.message || 'Erreur lors du chargement des demandes');
            console.error('Error fetching client EOIs:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const filterEOIs = () => {
        if (activeFilter === 'REJECTED') {
            setFilteredEois(eois.filter(eoi =>
                eoi.clientEOIStatus === 'REJECTED' || eoi.clientEOIStatus === 'CANCELLED'
            ));
        } else {
            setFilteredEois(eois.filter(eoi => eoi.clientEOIStatus === activeFilter));
        }
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    // ✅ Récupération de l'URL de l'image du service
    const getServiceImage = (eoi) => {
        if (eoi.mediaSubscriptions && eoi.mediaSubscriptions.length > 0) {
            const media = eoi.mediaSubscriptions[0];
            return media.urlPresigned || media.url;
        }
        return "/assets/images/default-service.jpg";
    };

    // ✅ Récupération de la photo de profil du professionnel
    const getProfessionalProfilePicture = (professional) => {
        if (!professional) return null;
        if (professional.profilePhotoUrlPresigned) {
            return professional.profilePhotoUrlPresigned;
        }
        if (professional.profilePhotoUrl) {
            return professional.profilePhotoUrl;
        }
        if (professional.user?.profilePictureUrl) {
            return professional.user.profilePictureUrl;
        }
        return null;
    };

    // ✅ Gestion Lightbox - Version corrigée
    const handleOpenLightbox = (images, index = 0) => {
        console.log('📸 Opening Lightbox with images:', images); // Debug
        setLightboxImages(images);
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const handleCloseLightbox = () => {
        console.log('📸 Closing Lightbox'); // Debug
        setLightboxOpen(false);
        setLightboxImages([]);
        setLightboxIndex(0);
    };

    const createContractFromEOI = async (eoi) => {
        try {
            console.log('=== DÉBUT CRÉATION CONTRAT ===');
            console.log('EOI reçu:', eoi);

            const username = localStorage.getItem('username') || 'manager';

            const today = new Date();
            const startDate = today.toISOString();

            const contractDuration = eoi.contractDuration || 1;
            const endDateObj = new Date(today);
            endDateObj.setMonth(today.getMonth() + contractDuration);
            endDateObj.setDate(endDateObj.getDate() - 1);
            const endDate = endDateObj.toISOString();

            console.log('StartDate utilisée:', startDate);
            console.log('EndDate calculée:', endDate);

            const professional = eoi.professional;
            const serviceName = professional?.selectedService || eoi.serviceName || 'Service non spécifié';
            const subServices = professional?.subServices;
            const subServiceName = subServices ?
                (subServices.split(';')[0] || subServices.split(',')[0] || subServices) :
                'Sous-service non spécifié';

            const getFrequency = () => {
                if (serviceName.includes('quotidien') || serviceName.includes('daily')) return 'QUOTIDIENNE';
                if (serviceName.includes('hebdomadaire') || serviceName.includes('weekly')) return 'HEBDOMADAIRE';
                if (serviceName.includes('mensuelle') || serviceName.includes('monthly')) return 'MENSUELLE';
                return 'MENSUELLE';
            };

            const contractRequestDTO = {
                startDate: startDate,
                endDate: endDate,
                contractStatus: "DRAFT",
                clientSignDate: null,
                dhsSignDate: null,
                generatedContract: "",
                contractDocClientSigned: "",
                contractClientActive: "",
                contractRef: "",
                clientSigned: false,
                dhsSignedClient: false,
                professionalId: professional?.id,
                clientId: eoi.client?.id || eoi.clientId,
                clientEOIId: eoi.id,
                serviceFee: eoi.serviceFee?.toString() || "0",
                contractDuration: contractDuration.toString(),
                serviceName: serviceName,
                subServiceName: subServiceName,
                frequency: eoi.frequency || getFrequency(),
                serviceLocation: eoi.serviceLocation ||
                    `${eoi.client?.user?.city || ''}, ${eoi.client?.user?.country || ''}`.trim(),
                createdBy: username,
                lastModifiedBy: username
            };

            console.log('DTO à envoyer:', contractRequestDTO);

            if (!contractRequestDTO.startDate || !contractRequestDTO.endDate) {
                throw new Error('Dates manquantes dans le DTO');
            }

            const response = await ContractService.createContract(contractRequestDTO);
            console.log('Réponse du backend:', response);

            return true;
        } catch (error) {
            console.error('Erreur complète:', error);
            console.error('Message d\'erreur:', error.message);
            if (error.response) {
                console.error('Réponse backend:', error.response.data);
            }
            throw error;
        }
    };

    const handleApproveRequest = async (eoi) => {
        if (!eoi) return;

        const result = await Swal.fire({
            title: 'Confirmer l\'approbation',
            html: `
                <div class="text-start">
                    <p>Êtes-vous sûr de vouloir approuver cette demande client ?</p>
                    <div class="alert alert-success mt-3">
                        Cette action approuvera la demande et créera automatiquement un contrat.
                    </div>
                    <div class="mt-3">
                        <strong>Détails de la demande:</strong><br/>
                        • Client: ${eoi.client?.user?.firstName || 'Non'} ${eoi.client?.user?.lastName || 'spécifié'}<br/>
                        • Professionnel: ${eoi.professional?.user?.firstName} ${eoi.professional?.user?.lastName}<br/>
                        • Service: ${eoi.serviceName || eoi.professional?.selectedService}<br/>
                        • Durée: ${eoi.contractDuration || 1} mois<br/>
                        • Tarif: ${formatCurrency(eoi.serviceFee || 0)}
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, approuver et créer le contrat',
            cancelButtonText: 'Annuler',
            customClass: {
                popup: 'swal2-popup-custom'
            }
        });

        if (result.isConfirmed) {
            setIsApproving(eoi.id);
            try {
                const username = localStorage.getItem('username') || 'admin';

                await createContractFromEOI(eoi);

                const updateData = {
                    comments: eoi.comments || "Demande validée avec succès - Contrat créé",
                    clientEOIStatus: "APPROVED",
                    userValidated: username,
                    lastModifiedBy: username,
                    clientId: eoi.client?.id || eoi.clientId,
                    professionalId: eoi.professional?.id || eoi.professionalId,
                };

                const updatedEOI = await ClientEOIService.updateClientEOI(eoi.id, updateData);
                setEois(eois.map(item => item.id === eoi.id ? updatedEOI : item));

                Swal.fire({
                    title: 'Succès !',
                    html: `
                        <div class="text-center">
                            <div class="mb-3">
                                <i class="fas fa-check-circle" style="font-size: 48px; color: #28a745;"></i>
                            </div>
                            <h5>Demande approuvée et contrat créé !</h5>
                            <p class="text-muted">La demande a été validée et le contrat a été créé avec succès.</p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonColor: '#3085d6'
                });
            } catch (error) {
                console.error('Error approving EOI:', error);

                let errorMessage = 'Une erreur est survenue lors de la validation.';
                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                Swal.fire('Erreur !', errorMessage, 'error');
            } finally {
                setIsApproving(null);
            }
        }
    };

    const handleRejectRequest = (eoi) => {
        setSelectedEOIForReject(eoi);
        setRejectionReason('');
        setShowRejectModal(true);
    };

    const confirmRejectRequest = async () => {
        if (!selectedEOIForReject) return;

        if (!rejectionReason.trim()) {
            Swal.fire('Erreur', 'Veuillez saisir le motif du rejet.', 'error');
            return;
        }

        setCancellingId(selectedEOIForReject.id);
        try {
            const username = localStorage.getItem('username') || 'admin';
            const cancelData = {
                lastModifiedBy: username,
                userRejected: username,
                rejectionReason: rejectionReason,
                clientEOIStatus: "REJECTED"
            };

            const updatedEOI = await ClientEOIService.updateClientEOI(selectedEOIForReject.id, cancelData);
            setEois(eois.map(item => item.id === selectedEOIForReject.id ? updatedEOI : item));

            Swal.fire({
                title: 'Demande rejetée !',
                text: 'La demande client a été rejetée avec succès.',
                icon: 'success',
                confirmButtonColor: '#3085d6'
            });

            setShowRejectModal(false);
            setSelectedEOIForReject(null);
            setRejectionReason('');
        } catch (error) {
            console.error('Error rejecting EOI:', error);
            Swal.fire('Erreur !', 'Une erreur est survenue lors du rejet.', 'error');
        } finally {
            setCancellingId(null);
        }
    };

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setSelectedEOIForReject(null);
        setRejectionReason('');
    };

    const handleViewDetails = (eoi) => {
        setSelectedEOI(eoi);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedEOI(null);
    };

    const handleUpdate = (updatedEOI) => {
        setEois(eois.map(eoi => eoi.id === updatedEOI.id ? updatedEOI : eoi));
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning" className="fs-6 py-2 px-3 rounded-pill">
                    <FaClock className="me-1" /> En attente
                </Badge>;
            case 'APPROVED':
                return <Badge bg="success" className="fs-6 py-2 px-3 rounded-pill">
                    <FaCheckCircle className="me-1" /> Accepté
                </Badge>;
            case 'REJECTED':
                return <Badge bg="danger" className="fs-6 py-2 px-3 rounded-pill">
                    <FaBan className="me-1" /> Rejeté
                </Badge>;
            case 'CANCELLED':
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">
                    <FaBan className="me-1" /> Annulé
                </Badge>;
            default:
                return <Badge bg="secondary" className="fs-6 py-2 px-3 rounded-pill">Inconnu</Badge>;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'PENDING': return 'En attente';
            case 'APPROVED': return 'Accepté';
            case 'REJECTED': return 'Rejeté';
            case 'CANCELLED': return 'Annulé';
            default: return 'Inconnu';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date inconnue';
        try {
            if (Array.isArray(dateString) && dateString.length === 3) {
                const [year, month, day] = dateString;
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            }
            if (typeof dateString === 'string' && dateString.includes('-')) {
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    const day = parseInt(parts[0]);
                    const month = parseInt(parts[1]);
                    const year = parseInt(parts[2]);
                    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                        });
                    }
                }
            }
            if (typeof dateString === 'string' && dateString.includes('T')) {
                const date = new Date(dateString);
                return date.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                });
            }
            return 'Date invalide';
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Date invalide';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount) + ' FCFA';
    };

    const getFilterStats = () => {
        return {
            PENDING: eois.filter(e => e.clientEOIStatus === 'PENDING').length,
            APPROVED: eois.filter(e => e.clientEOIStatus === 'APPROVED').length,
            REJECTED: eois.filter(e =>
                e.clientEOIStatus === 'REJECTED' || e.clientEOIStatus === 'CANCELLED'
            ).length
        };
    };

    const filterStats = getFilterStats();

    return {
        eois,
        filteredEois,
        activeFilter,
        isLoading,
        error,
        selectedEOI,
        isModalOpen,
        cancellingId,
        creatingContractId,
        isApproving,
        showRejectModal,
        selectedEOIForReject,
        rejectionReason,
        lightboxOpen,
        lightboxImages,
        lightboxIndex,
        filterStats,
        getStatusBadge,
        getStatusText,
        formatDate,
        formatCurrency,
        getServiceImage,
        getProfessionalProfilePicture,
        handleFilterChange,
        handleViewDetails,
        handleUpdate,
        handleCloseModal,
        handleApproveRequest,
        handleRejectRequest,
        confirmRejectRequest,
        closeRejectModal,
        setRejectionReason,
        handleOpenLightbox,
        handleCloseLightbox,
        fetchClientEOIs
    };
};

export default useValidationRequest;